// =============================================================================
// "Top Picks for You" ranking — the single place recommendation logic lives.
//
// Pure functions only (no Firestore, no React) so it is unit-testable and the
// Events screen, and anything else that later wants recommendations, share one
// implementation. Data loading is in services/userEventSignals.ts; the React
// glue is hooks/useTopPicks.ts.
//
// Score (each term is 0..1 before its weight; max total = 23):
//
//   explicitInterest    * 5   profile interests / custom interests / hashtags /
//                             music taste matched against the event
//   categoryInteraction * 3   how much the user's own actions concentrate on
//                             this event's category
//   tagInteraction      * 3   same, for tags / hashtags / music genres
//   joinedOrInterested  * 4   similarity to events the user joined or marked
//                             interested (same category, tags, host)
//   shared              * 2   similarity to events the user shared
//   viewed              * 1   similarity to events the user opened
//   location            * 2   distance from the user, or same selected city
//   date                * 2   how soon the event starts
//   popularity          * 1   interested / going / likes / shares, log-scaled
//                             against the other candidates
//
// Hard exclusions (never recommended): unavailable events (deleted, expired,
// cancelled, pending, private), events the user created, events that banned or
// removed the user, events hosted by someone the user blocked, events the user
// is already interested in or joined, and any id in `excludeEventIds` (the
// screen passes the Featured This Week ids so an event is never in both).
// =============================================================================

import { INTEREST_CATEGORIES } from '../data/interests';
import { INTEREST_GROUP_TARGETS, INTEREST_SYNONYMS } from '../data/interestEventMap';
import type { EventCategory, TonyteActivity } from '../types';
import { parseActivityDate } from './activityDates';
import { isEventAvailableForDiscovery } from './eventAvailability';
import { getEventCoordinates } from './eventEligibility';
import { getResolvedEventId } from './eventIds';
import { normalizeCityKey } from './cities';
import { haversineKm } from './geo';

export const RECOMMENDATION_WEIGHTS = {
  explicitInterest: 5,
  categoryInteraction: 3,
  tagInteraction: 3,
  joinedOrInterested: 4,
  shared: 2,
  viewed: 1,
  location: 2,
  date: 2,
  popularity: 1,
} as const;

// How strongly one past action of each kind shapes the user's taste profile.
// Joining, and marking interest, outweigh sharing, which outweighs merely
// opening an event.
export const ACTION_WEIGHTS = { joined: 4, interested: 3, shared: 2, viewed: 1 } as const;

export const DEFAULT_TOP_PICKS_LIMIT = 8;
// Below this a carousel reads as a mistake rather than a recommendation.
export const MIN_TOP_PICKS = 3;
const DEFAULT_HORIZON_DAYS = 30;
const CANDIDATE_CAP = 300;

export type RecommendationProfile = {
  interests?: string[];
  customInterests?: string[];
  hashtags?: string[];
  musicTaste?: string[];
  goingOutStyle?: string[];
};

export type RecommendationHistory = {
  joined: TonyteActivity[];
  interested: TonyteActivity[];
  shared: TonyteActivity[];
  viewed: TonyteActivity[];
};

export const EMPTY_HISTORY: RecommendationHistory = { joined: [], interested: [], shared: [], viewed: [] };

export type ScoreBreakdown = {
  explicitInterest: number;
  categoryInteraction: number;
  tagInteraction: number;
  joinedOrInterested: number;
  shared: number;
  viewed: number;
  location: number;
  date: number;
  popularity: number;
};

export type RankedEvent = {
  activity: TonyteActivity;
  score: number;
  breakdown: ScoreBreakdown;
};

export type TopPicksMode = 'personalized' | 'popular';

export type TopPicksResult = {
  items: RankedEvent[];
  mode: TopPicksMode;
};

export type RecommendationInput = {
  candidates: TonyteActivity[];
  userId: string | null;
  profile?: RecommendationProfile | null;
  history?: RecommendationHistory | null;
  excludeEventIds?: Iterable<string>;
  blockedUserIds?: Iterable<string>;
  userCoords?: { latitude: number; longitude: number } | null;
  cityKey?: string | null;
  now?: Date;
  limit?: number;
  horizonDays?: number;
};

// ---------------------------------------------------------------------------
// Event features (memoised per activity object)
// ---------------------------------------------------------------------------

type EventFeatures = {
  category: EventCategory;
  tags: Set<string>;
  hostKey: string | null;
  text: string;
  startMs: number | null;
  endMs: number | null;
};

const featureCache = new WeakMap<TonyteActivity, EventFeatures>();

function normalizeToken(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/^#/, '');
}

function getHostKey(activity: TonyteActivity): string | null {
  const candidates = [activity.venueId, activity.organizerId, activity.primaryHostId, activity.ownerUid, activity.hostId];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate && candidate !== 'system') return candidate;
  }
  return null;
}

function getFeatures(activity: TonyteActivity): EventFeatures {
  const cached = featureCache.get(activity);
  if (cached) return cached;
  const tags = new Set<string>();
  [activity.tags, activity.hashtags, activity.musicGenres].forEach(list => {
    (list || []).forEach(item => {
      const token = normalizeToken(item);
      if (token) tags.add(token);
    });
  });
  const parsed = parseActivityDate(activity);
  const features: EventFeatures = {
    category: activity.category,
    tags,
    hostKey: getHostKey(activity),
    text: [
      activity.title,
      activity.description,
      activity.category,
      activity.venueName,
      activity.clubName,
      ...(activity.lineup || []),
      ...tags,
    ].filter(Boolean).join(' ').toLowerCase(),
    startMs: parsed.startAt?.getTime() ?? null,
    endMs: parsed.endAt?.getTime() ?? null,
  };
  featureCache.set(activity, features);
  return features;
}

// ---------------------------------------------------------------------------
// Explicit interests (profile)
// ---------------------------------------------------------------------------

type InterestProfile = {
  matchers: RegExp[];
  categoryWeights: Map<string, number>;
  tagWeights: Map<string, number>;
  isEmpty: boolean;
};

const INTEREST_TO_GROUP = new Map<string, string>();
INTEREST_CATEGORIES.forEach(group => {
  group.interests.forEach(interest => INTEREST_TO_GROUP.set(interest.toLowerCase(), group.name));
});

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const matcherCache = new Map<string, RegExp>();
function buildMatcher(phrases: string[]): RegExp {
  const key = phrases.join('|');
  const cached = matcherCache.get(key);
  if (cached) return cached;
  const regex = new RegExp(`(?:^|[^a-z0-9])(?:${phrases.map(escapeRegExp).join('|')})(?:$|[^a-z0-9])`, 'i');
  matcherCache.set(key, regex);
  return regex;
}

function bump(map: Map<string, number>, key: string, amount: number) {
  map.set(key, (map.get(key) ?? 0) + amount);
}

function normalizeByMax(map: Map<string, number>): Map<string, number> {
  let max = 0;
  map.forEach(value => { if (value > max) max = value; });
  if (max <= 0) return new Map();
  const result = new Map<string, number>();
  map.forEach((value, key) => result.set(key, value / max));
  return result;
}

export function buildInterestProfile(profile: RecommendationProfile | null | undefined): InterestProfile {
  const matchers: RegExp[] = [];
  const categoryRaw = new Map<string, number>();
  const tagRaw = new Map<string, number>();
  const seen = new Set<string>();

  const addKeyword = (raw: unknown, groupName?: string) => {
    const interest = normalizeToken(raw);
    if (!interest || seen.has(interest)) return;
    seen.add(interest);
    matchers.push(buildMatcher(INTEREST_SYNONYMS[interest] ?? [interest]));
    const group = groupName ?? INTEREST_TO_GROUP.get(interest);
    const target = group ? INTEREST_GROUP_TARGETS[group] : undefined;
    if (target) {
      target.categories.forEach(category => bump(categoryRaw, category, 1));
      target.tags.forEach(tag => bump(tagRaw, tag, 1));
    }
  };

  (profile?.interests || []).forEach(item => addKeyword(item));
  (profile?.customInterests || []).forEach(item => addKeyword(item));
  (profile?.hashtags || []).forEach(item => addKeyword(item));
  (profile?.goingOutStyle || []).forEach(item => addKeyword(item));
  // Music taste is a music-group signal even when the label isn't one of the
  // built-in Music interests (e.g. "Afrobeats").
  (profile?.musicTaste || []).forEach(item => addKeyword(item, 'Music'));

  return {
    matchers,
    categoryWeights: normalizeByMax(categoryRaw),
    tagWeights: normalizeByMax(tagRaw),
    isEmpty: matchers.length === 0,
  };
}

function scoreExplicitInterest(features: EventFeatures, interests: InterestProfile): number {
  if (interests.isEmpty) return 0;
  let hits = 0;
  for (const matcher of interests.matchers) {
    if (matcher.test(features.text)) hits += 1;
  }
  const keywordScore = 1 - Math.pow(0.5, hits);
  const categoryScore = interests.categoryWeights.get(features.category) ?? 0;
  let tagScore = 0;
  features.tags.forEach(tag => {
    tagScore = Math.max(tagScore, interests.tagWeights.get(tag) ?? 0);
  });
  const structural = Math.max(categoryScore, 0.8 * tagScore);
  return Math.min(1, 0.55 * keywordScore + 0.6 * structural);
}

// ---------------------------------------------------------------------------
// Behavioural taste profile (from the user's own actions)
// ---------------------------------------------------------------------------

type WeightedEvent = { activity: TonyteActivity; weight: number };

type TasteProfile = {
  categoryAffinity: Map<string, number>;
  tagAffinity: Map<string, number>;
  strong: TonyteActivity[];
  shared: TonyteActivity[];
  viewed: TonyteActivity[];
  isEmpty: boolean;
};

function eventKey(activity: TonyteActivity): string {
  return getResolvedEventId(activity) || activity.id;
}

export function buildTasteProfile(history: RecommendationHistory | null | undefined): TasteProfile {
  const h = history ?? EMPTY_HISTORY;
  // One event can appear under several actions (joining always implies
  // interest). Its weight is the strongest action plus a quarter of each
  // additional one, so it isn't double-counted as two separate events.
  const perEvent = new Map<string, { activity: TonyteActivity; weights: number[] }>();
  const add = (list: TonyteActivity[], weight: number) => {
    list.forEach(activity => {
      const key = eventKey(activity);
      const entry = perEvent.get(key);
      if (entry) entry.weights.push(weight);
      else perEvent.set(key, { activity, weights: [weight] });
    });
  };
  add(h.joined, ACTION_WEIGHTS.joined);
  add(h.interested, ACTION_WEIGHTS.interested);
  add(h.shared, ACTION_WEIGHTS.shared);
  add(h.viewed, ACTION_WEIGHTS.viewed);

  const weighted: WeightedEvent[] = [];
  perEvent.forEach(({ activity, weights }) => {
    const sorted = [...weights].sort((a, b) => b - a);
    const weight = sorted[0] + sorted.slice(1).reduce((sum, w) => sum + 0.25 * w, 0);
    weighted.push({ activity, weight });
  });

  const categoryRaw = new Map<string, number>();
  const tagRaw = new Map<string, number>();
  weighted.forEach(({ activity, weight }) => {
    const features = getFeatures(activity);
    bump(categoryRaw, features.category, weight);
    features.tags.forEach(tag => bump(tagRaw, tag, weight));
  });

  return {
    categoryAffinity: normalizeByMax(categoryRaw),
    tagAffinity: normalizeByMax(tagRaw),
    strong: [...h.joined, ...h.interested],
    shared: h.shared,
    viewed: h.viewed,
    isEmpty: weighted.length === 0,
  };
}

function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  a.forEach(value => { if (b.has(value)) intersection += 1; });
  return intersection / (a.size + b.size - intersection);
}

// Best single resemblance between a candidate and any event in `history`
// (max, not sum — a long history must not swamp the fixed-weight terms).
function historySimilarity(features: EventFeatures, history: TonyteActivity[]): number {
  let best = 0;
  for (const past of history) {
    const pastFeatures = getFeatures(past);
    const similarity =
      (features.category === pastFeatures.category ? 0.45 : 0) +
      0.35 * jaccard(features.tags, pastFeatures.tags) +
      (features.hostKey && features.hostKey === pastFeatures.hostKey ? 0.2 : 0);
    if (similarity > best) best = similarity;
    if (best >= 1) break;
  }
  return best;
}

function scoreTagInteraction(features: EventFeatures, taste: TasteProfile): number {
  if (taste.tagAffinity.size === 0) return 0;
  let first = 0;
  let second = 0;
  features.tags.forEach(tag => {
    const affinity = taste.tagAffinity.get(tag) ?? 0;
    if (affinity > first) { second = first; first = affinity; }
    else if (affinity > second) second = affinity;
  });
  return Math.min(1, first * 0.7 + second * 0.3);
}

// ---------------------------------------------------------------------------
// Context terms: location, date, popularity
// ---------------------------------------------------------------------------

const SAME_CITY_BASELINE = 0.5;

function scoreLocation(
  activity: TonyteActivity,
  cityKey: string,
  userCoords: RecommendationInput['userCoords'],
): number {
  // Every candidate is already in the selected city, which is the user's
  // explicit choice — so being in it is the floor, and physical distance only
  // ever adds to it. A user browsing Dresden from another country must not
  // have every event zeroed out by distance.
  const sameCity = cityKey && normalizeCityKey(activity.cityKey || activity.city) === cityKey;
  const floor = sameCity ? SAME_CITY_BASELINE : 0;
  if (!userCoords) return floor;
  const coords = getEventCoordinates(activity);
  if (!coords) return floor;
  const km = haversineKm(userCoords.latitude, userCoords.longitude, coords.latitude, coords.longitude);
  const byDistance = km <= 2 ? 1 : km <= 5 ? 0.85 : km <= 10 ? 0.6 : km <= 25 ? 0.35 : km <= 60 ? 0.15 : 0;
  return Math.max(floor, byDistance);
}

const HOUR_MS = 60 * 60 * 1000;

function scoreDate(features: EventFeatures, nowMs: number): number {
  if (features.startMs === null) return 0;
  if (features.startMs <= nowMs) {
    // Already started but not over (candidates are never past their end).
    return 0.9;
  }
  const hours = (features.startMs - nowMs) / HOUR_MS;
  if (hours <= 6) return 1;
  if (hours <= 24) return 0.95;
  if (hours <= 72) return 0.8;
  if (hours <= 24 * 7) return 0.6;
  if (hours <= 24 * 14) return 0.35;
  return 0.15;
}

function rawPopularity(activity: TonyteActivity): number {
  const attendance = Math.max(
    activity.interestedCount || 0,
    activity.participants || 0,
    activity.participantIds?.length || 0,
    activity.attendees?.length || 0,
  );
  return attendance + 2 * (activity.sharesCount || 0) + (activity.likesCount || 0) + 0.5 * (activity.commentsCount || 0);
}

// ---------------------------------------------------------------------------
// Eligibility + diversification
// ---------------------------------------------------------------------------

function ownsEvent(activity: TonyteActivity, userId: string): boolean {
  return [
    activity.createdBy, activity.hostId, activity.ownerUid, activity.organizerId,
    (activity as TonyteActivity & { uploadedBy?: string }).uploadedBy,
    (activity as TonyteActivity & { creatorId?: string }).creatorId,
  ].some(owner => owner === userId);
}

function isHostedBy(activity: TonyteActivity, blocked: Set<string>): boolean {
  if (blocked.size === 0) return false;
  return [activity.createdBy, activity.hostId, activity.ownerUid, activity.organizerId]
    .some(owner => typeof owner === 'string' && blocked.has(owner));
}

const MONTHS_AND_DAYS = /\b(jan(uary)?|feb(ruary)?|mar(ch)?|apr(il)?|may|jun(e)?|jul(y)?|aug(ust)?|sep(t(ember)?)?|oct(ober)?|nov(ember)?|dec(ember)?|mon(day)?|tue(s(day)?)?|wed(nesday)?|thu(r(s(day)?)?)?|fri(day)?|sat(urday)?|sun(day)?)\b/g;

// Recurring events are separate documents with the same title ("Cafe Lingua",
// week after week). Two of them side by side is the "nearly identical events"
// case, so they collapse to their soonest occurrence. Digits are kept in the
// key (only weekday/month words and punctuation are stripped) — two distinct
// events commonly differ only by a number in the title ("Trivia Night #1" vs
// "#2", "Top 40 Night", "Class of 2026"), and dropping digits made every such
// pair collide as "duplicates" of each other.
function duplicateKey(activity: TonyteActivity): string {
  const title = activity.title.toLowerCase().replace(MONTHS_AND_DAYS, ' ').replace(/[^a-z0-9À-ɏЀ-ӿ ]+/g, ' ').replace(/\s+/g, ' ').trim();
  const venue = String(activity.venueId || activity.venueName || activity.locationName || '').trim().toLowerCase();
  return `${title}|${venue}`;
}

const CATEGORY_REPEAT_PENALTY = 1.2;
const HOST_REPEAT_PENALTY = 2.5;

function diversify(ranked: RankedEvent[], limit: number): RankedEvent[] {
  const remaining = [...ranked];
  const picked: RankedEvent[] = [];
  const categoryCount = new Map<string, number>();
  const hostCount = new Map<string, number>();
  const seenDuplicates = new Set<string>();

  while (picked.length < limit && remaining.length > 0) {
    let bestIndex = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < remaining.length; i += 1) {
      const item = remaining[i];
      const features = getFeatures(item.activity);
      const adjusted =
        item.score
        - CATEGORY_REPEAT_PENALTY * (categoryCount.get(features.category) ?? 0)
        - HOST_REPEAT_PENALTY * (features.hostKey ? hostCount.get(features.hostKey) ?? 0 : 0);
      if (adjusted > bestScore) { bestScore = adjusted; bestIndex = i; }
    }
    const [chosen] = remaining.splice(bestIndex, 1);
    const key = duplicateKey(chosen.activity);
    if (seenDuplicates.has(key)) continue;
    seenDuplicates.add(key);
    const features = getFeatures(chosen.activity);
    bump(categoryCount, features.category, 1);
    if (features.hostKey) bump(hostCount, features.hostKey, 1);
    picked.push(chosen);
  }
  return picked;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function hasPersonalSignals(
  profile: RecommendationProfile | null | undefined,
  history: RecommendationHistory | null | undefined,
): boolean {
  return !buildInterestProfile(profile).isEmpty || !buildTasteProfile(history).isEmpty;
}

// ---------------------------------------------------------------------------
// Recommendation reason — display only. Picks the single dominant weighted
// signal behind an already-ranked item's score for a short "why this" line
// ("Because you like techno", "Matches your social interests"). Reads the
// exact breakdown rankTopPicks already produced; never re-derives, re-weighs
// or otherwise changes the score itself — a UI-layer explanation of a
// decision already made, not a second decision.
// ---------------------------------------------------------------------------

export type RecommendationReason =
  | { kind: 'explicitInterest'; interest: string }
  | { kind: 'category'; category: EventCategory }
  | { kind: 'joined' }
  | { kind: 'shared' }
  | { kind: 'viewed' }
  | { kind: 'popular' };

// Which single profile interest (original casing, for display) textually
// matched this event — i.e. which one is actually responsible for a nonzero
// explicitInterest score. Deliberately duplicates only the "which one
// matched" question, not scoreExplicitInterest's math, from the same
// profile fields and INTEREST_SYNONYMS table buildInterestProfile uses.
function findMatchedInterestLabel(activity: TonyteActivity, profile: RecommendationProfile | null | undefined): string | null {
  const text = getFeatures(activity).text;
  const sources = [...(profile?.interests || []), ...(profile?.customInterests || []), ...(profile?.hashtags || [])];
  const seen = new Set<string>();
  for (const raw of sources) {
    const original = String(raw ?? '').trim();
    if (!original) continue;
    const key = normalizeToken(original);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (buildMatcher(INTEREST_SYNONYMS[key] ?? [key]).test(text)) return original;
  }
  return null;
}

// A contribution below this (out of a term's own 0..1 range, before its
// weight) isn't meaningfully "why" this event was picked — falls through to
// the always-safe, never-overclaiming 'popular' reason instead.
const MIN_REASON_CONTRIBUTION = 0.3;

export function pickRecommendationReason(
  item: RankedEvent,
  profile: RecommendationProfile | null | undefined,
  mode: TopPicksMode,
): RecommendationReason {
  // No personal signal exists at all for this user — never imply one.
  if (mode === 'popular') return { kind: 'popular' };

  const W = RECOMMENDATION_WEIGHTS;
  const { breakdown } = item;
  const candidates: Array<{ reason: RecommendationReason; contribution: number; raw: number }> = [];

  const interest = findMatchedInterestLabel(item.activity, profile);
  if (interest) {
    candidates.push({ reason: { kind: 'explicitInterest', interest }, contribution: breakdown.explicitInterest * W.explicitInterest, raw: breakdown.explicitInterest });
  }
  const categoryRaw = Math.max(breakdown.categoryInteraction, breakdown.tagInteraction);
  candidates.push({
    reason: { kind: 'category', category: item.activity.category },
    contribution: categoryRaw * Math.max(W.categoryInteraction, W.tagInteraction),
    raw: categoryRaw,
  });
  candidates.push({ reason: { kind: 'joined' }, contribution: breakdown.joinedOrInterested * W.joinedOrInterested, raw: breakdown.joinedOrInterested });
  candidates.push({ reason: { kind: 'shared' }, contribution: breakdown.shared * W.shared, raw: breakdown.shared });
  candidates.push({ reason: { kind: 'viewed' }, contribution: breakdown.viewed * W.viewed, raw: breakdown.viewed });
  candidates.push({
    reason: { kind: 'popular' },
    contribution: breakdown.popularity * W.popularity + breakdown.location * W.location,
    raw: Math.max(breakdown.popularity, breakdown.location),
  });

  let best = candidates[0];
  for (const candidate of candidates) {
    if (candidate.contribution > best.contribution) best = candidate;
  }
  return best.raw >= MIN_REASON_CONTRIBUTION ? best.reason : { kind: 'popular' };
}

export function rankTopPicks(input: RecommendationInput): TopPicksResult {
  const now = input.now ?? new Date();
  const nowMs = now.getTime();
  const limit = input.limit ?? DEFAULT_TOP_PICKS_LIMIT;
  const horizonMs = nowMs + (input.horizonDays ?? DEFAULT_HORIZON_DAYS) * 24 * HOUR_MS;
  const cityKey = input.cityKey ? normalizeCityKey(input.cityKey) : '';

  const interests = buildInterestProfile(input.profile);
  const taste = buildTasteProfile(input.history);
  const mode: TopPicksMode = !interests.isEmpty || !taste.isEmpty ? 'personalized' : 'popular';

  const excluded = new Set<string>(input.excludeEventIds ?? []);
  const blocked = new Set<string>(input.blockedUserIds ?? []);
  // Already-interested / joined events are excluded outright: the carousel is
  // for discovering something new, and the regular feed already shows those
  // with their active state.
  [...(input.history?.joined ?? []), ...(input.history?.interested ?? [])].forEach(activity => {
    excluded.add(eventKey(activity));
    excluded.add(activity.id);
  });

  const candidates = input.candidates
    .filter(activity => {
      if (excluded.has(activity.id) || excluded.has(eventKey(activity))) return false;
      if (!isEventAvailableForDiscovery(activity, now)) return false;
      if (cityKey && normalizeCityKey(activity.cityKey || activity.city) !== cityKey) return false;
      if (input.userId) {
        if (ownsEvent(activity, input.userId)) return false;
        if (activity.bannedUserIds?.includes(input.userId) || activity.removedUserIds?.includes(input.userId)) return false;
      }
      if (isHostedBy(activity, blocked)) return false;
      const startMs = getFeatures(activity).startMs;
      return startMs === null || startMs <= horizonMs;
    })
    .sort((a, b) => (getFeatures(a).startMs ?? Infinity) - (getFeatures(b).startMs ?? Infinity))
    .slice(0, CANDIDATE_CAP);

  if (candidates.length === 0) return { items: [], mode };

  const popularityRaw = candidates.map(rawPopularity);
  const maxPopularity = Math.max(...popularityRaw, 0);
  const popularityDenominator = Math.log1p(maxPopularity);

  const W = RECOMMENDATION_WEIGHTS;
  const ranked: RankedEvent[] = candidates.map((activity, index) => {
    const features = getFeatures(activity);
    const breakdown: ScoreBreakdown = {
      explicitInterest: scoreExplicitInterest(features, interests),
      categoryInteraction: taste.categoryAffinity.get(features.category) ?? 0,
      tagInteraction: scoreTagInteraction(features, taste),
      joinedOrInterested: taste.strong.length ? historySimilarity(features, taste.strong) : 0,
      shared: taste.shared.length ? historySimilarity(features, taste.shared) : 0,
      viewed: taste.viewed.length ? historySimilarity(features, taste.viewed) : 0,
      location: scoreLocation(activity, cityKey, input.userCoords),
      date: scoreDate(features, nowMs),
      popularity: popularityDenominator > 0 ? Math.log1p(popularityRaw[index]) / popularityDenominator : 0,
    };
    const score =
      breakdown.explicitInterest * W.explicitInterest +
      breakdown.categoryInteraction * W.categoryInteraction +
      breakdown.tagInteraction * W.tagInteraction +
      breakdown.joinedOrInterested * W.joinedOrInterested +
      breakdown.shared * W.shared +
      breakdown.viewed * W.viewed +
      breakdown.location * W.location +
      breakdown.date * W.date +
      breakdown.popularity * W.popularity;
    return { activity, score, breakdown };
  });

  ranked.sort((a, b) => b.score - a.score || (getFeatures(a.activity).startMs ?? Infinity) - (getFeatures(b.activity).startMs ?? Infinity));
  return { items: diversify(ranked, limit), mode };
}
